import { call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import {
  REQUEST,
  fetchSuccess,
  fetchFailure,
  marketplaceConflict,
  clearMarketplaceConflict,
} from '../../actions/workspaces/delete';
import config from '../../../config';
import { getLocalUser } from '../../../utils/localUser';
import { Error } from '../../actions/error/error';
import { fetchRequest as fetchWorkspaces } from '../../actions/workspaces/list';
import { fetchRequest as fetchUser } from '../../actions/user/getUser';
import { extractErrorMessage } from '../../../utils/apiResponseHandler';

function* operation(action) {
  const { workspaceId, force } = action;
  // Fresh request -> drop any stale conflict so the dialog doesn't
  // re-open on a subsequent attempt.
  yield put(clearMarketplaceConflict());
  try {
    const url =
      `${config.API_URL}/workspace/${workspaceId}`
      + (force ? '?force=true' : '');
    const response = yield call(axios.delete, url, {
      headers: getLocalUser(),
    });
    yield put(fetchSuccess(response.data));

    yield put({
      type: 'SHOW_SUCCESS_SNACKBAR',
      message: 'Workspace deleted successfully',
    });

    // Keep both workspace-list sources in sync: the on-demand
    // workspace_list slice AND the user payload that feeds
    // WorkspaceContext (header switcher, dashboard).
    yield put(fetchWorkspaces());
    yield put(fetchUser());
  } catch (error) {
    const status = error?.response?.status;
    // BE wraps every responseJSON payload as ``{ data: <payload> }``
    // (see core/src/utils/api_utils.py::responseJSON). Tolerate both
    // shapes so we don't depend on a future refactor changing this.
    const raw = error?.response?.data || {};
    const body = raw?.data && typeof raw.data === 'object' ? raw.data : raw;
    // Canonical envelope: {error: {code, message, details}}; tolerate the
    // older flat-string form as well.
    const errCode = typeof body?.error === 'object' ? body.error?.code : body?.error;
    const errMessage =
      (typeof body?.error === 'object' && body.error?.message) || body?.message;

    // 409 marketplace conflict is an interactive prompt, not an
    // error. Two flavors:
    //   - active_marketplace_subscribers: severe, cancels paying subs
    //   - published_marketplace_listing : advisory, just delists
    // Both surface listings + sub summary so the cascade dialog
    // can render the right copy. Crucially: do NOT route through
    // the global error handler / toast - that would frame this as
    // a failure, when it's just a "are you sure?" branch.
    const isMarketplaceConflict =
      status === 409 && (
        errCode === 'active_marketplace_subscribers'
        || errCode === 'published_marketplace_listing'
      );
    if (isMarketplaceConflict) {
      yield put(marketplaceConflict({
        workspaceId,
        kind:               errCode,
        message:            errMessage,
        active_subscribers: body.active_subscribers,
        listings:           body.listings || [],
      }));
      yield put(fetchFailure({ conflict: true }));
      return;
    }

    console.error('Delete workspace error:', error);
    yield put(Error(error));
    yield put(fetchFailure(extractErrorMessage(error)));

    if (status === 403) {
      yield put({
        type: 'SHOW_ERROR_SNACKBAR',
        message: 'You don\'t have permission to delete this workspace',
      });
    } else if (status === 404) {
      yield put({
        type: 'SHOW_ERROR_SNACKBAR',
        message: 'Workspace not found',
      });
    } else if (status === 400 && error?.response?.data?.message?.includes('models')) {
      yield put({
        type: 'SHOW_ERROR_SNACKBAR',
        message: 'Cannot delete workspace with existing models. Please delete all models first.',
      });
    } else {
      yield put({
        type: 'SHOW_ERROR_SNACKBAR',
        message: 'Failed to delete workspace',
      });
    }
  }
}

export function* watch() {
  yield takeLatest(REQUEST, operation);
}
